import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  getDepartmentLists,
  createDepartmentList,
  updateDepartmentList,
} from '../../../Api/request/departmentList';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { departmentListTableConfig } from './departmentListTableConfig';
import './departmentList.scss';

const EMPTY_FORM = { name: '' };
const EMPTY_PERMISSIONS = [];

const getApiErrorMessage = (response) => {
  const payload = response?.data;
  if (payload instanceof Error) {
    return payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || payload.message
      || 'Something went wrong.';
  }
  if (payload?.status === false) {
    return payload?.message
      || payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || 'Something went wrong.';
  }
  return '';
};

const normalizeDepartmentLists = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || response?.data?.department || [];

  return rows.map((department) => normalizeDepartment(department));
};

const normalizeDepartment = (department) => ({
  id: department.department_id,
  name: department.department_name || '',
  isActive: Boolean(department.is_active),
});

const DepartmentForm = ({ mode, form, error, isSubmitDisabled, onChange, onClose, onSubmit }) => createPortal(
  <div className="department-modal-backdrop">
    <form className="department-modal" onSubmit={onSubmit}>
      <div className="department-modal__header">
        {mode === 'edit' ? 'Edit Department' : 'Add Department'}
      </div>
      <div className="department-modal__body">
        <label htmlFor="department-name" className={error ? 'is-error' : ''}>
          Department Name<span className="required-asterisk">*</span>
        </label>
        <input
          id="department-name"
          name="name"
          type="text"
          placeholder="Enter Department Name"
          value={form.name}
          onChange={onChange}
          className={error ? 'is-error' : ''}
          autoComplete="off"
        />
      </div>
      <div className="department-modal__footer">
        <button type="button" className="department-modal__btn department-modal__btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="department-modal__btn department-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const DepartmentList = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_DEPARTMENT_LIST];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => (permission || EMPTY_PERMISSIONS).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);
  const [ departments, setDepartments ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleDepartment, setToggleDepartment] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState(false);

  const loadDepartmentLists = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getDepartmentLists(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setDepartments(normalizeDepartmentLists(response));
  }, [hasAccess]);

  useEffect(() => {
    loadDepartmentLists();
  }, [loadDepartmentLists]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, [hasAccess]);

  const openEdit = useCallback((provider) => {
    if (!hasAccess('update')) return;
    setDialog({ isOpen: true, mode: 'edit', id: provider.id });
    setForm({ name: provider.name });
    setHasError(false);
  }, [hasAccess]);

  const selectedDepartment = departments.find((provider) => provider.id === dialog.id);
  const isSubmitDisabled = !form.name.trim()
    || (dialog.mode === 'edit' && form.name.trim() === selectedDepartment?.name.trim());

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const name = form.name.trim();
    if (!name) {
      setHasError(true);
      toast.error('Please enter a department name.');
      return;
    }

    const response = dialog.mode === 'edit'
      ? await UseAxios(
        updateDepartmentList(dialog.id, name, selectedDepartment?.isActive ?? true),
        setIsLoading,
      )
      : await UseAxios(createDepartmentList(name), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Department updated successfully.'
      : 'Department added successfully.');
    const savedDepartment = normalizeDepartment(response?.data?.data || response.data);
    setDepartments((currentDepartments) => (
      dialog.mode === 'edit'
        ? currentDepartments.map((department) => (
          department.id === savedDepartment.id ? savedDepartment : department
        ))
        : [...currentDepartments, savedDepartment]
    ));
    closeForm();
  }, [closeForm, dialog.id, dialog.mode, form.name, hasAccess, selectedDepartment]);


  const handleToggle = useCallback((department) => {
    if (!hasAccess('update')) return;
    setToggleDepartment(department);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleDepartment(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleDepartment || !hasAccess('update')) return;

    const nextStatus = !toggleDepartment.isActive;
    const response = await UseAxios(
      updateDepartmentList(toggleDepartment.id, toggleDepartment.name, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setDepartments((currentDepartments) => currentDepartments.map((department) => (
      department.id === toggleDepartment.id
        ? { ...department, isActive: nextStatus }
        : department
    )));
    toast.success(`${toggleDepartment.name} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleDepartment]);

  const tableData = useMemo(() => departments.map((department, index) => ({
    ...department,
    slNo: index + 1,
    departmentList: department.name,
    status: department.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="department-list-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={department.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(department)}
        />
        <button
          type="button"
          className="department-list-action-btn"
          title="Edit Department"
          aria-label={`Edit ${department.name}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(department)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [handleToggle, hasAccess, openEdit, departments]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Department List',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: departmentListTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: departmentListTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view department lists.</div>;
  }

  return (
    <div className="department-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <DepartmentForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          onChange={(event) => {
            setForm({ name: event.target.value });
            setHasError(false);
          }}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleDepartment)}
        title={`${toggleDepartment?.isActive ? 'Disable' : 'Enable'} Department List`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${toggleDepartment?.isActive ? 'disable' : 'enable'} this department?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-cloud-provider-toggle"
        button2={toggleDepartment?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-cloud-provider-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default DepartmentList;
